import { Divider, Flex, Typography } from 'antd';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import Ticket from './Ticket.png';
import KamereoLogo from './KamereoLogo.png';

import ActiveTicket from './ActiveTicket.png';

import clock from './clock.svg';
import gift from './gift.svg';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import Button from 'components/Button/Button';
import translations from 'translations';
import messages from './messages';
import { Voucher } from 'types/schema';
import { FormattedMessage } from 'react-intl';
import Money from 'components/Money/Money';
import handleDateFromGraphQL from 'utils/formatDateTime';
import { VOUCHER_STATUS } from 'containers/CheckoutPage/constants';
import { WarningOutlined } from '@ant-design/icons';
import { Viewport } from 'components/ViewportProvider';

const Wrapper = styled.div`
  padding-top: 32%;
  position: relative;
  background-image: ${({ isActive }) => (isActive ? `url(${ActiveTicket})` : `url(${Ticket})`)};
  background-size: cover;
  background-repeat: no-repeat;
  ${({ isDisabled }) =>
    isDisabled &&
    `
      opacity: 0.5;
    `}
`;

const VoucherInfoWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0px;
  width: 100%;
  height: 100%;
  padding: 10px;
  gap: 16px;
`;

const KamereoLogoImage = styled.div`
  position: relative;
  background-image: url(${KamereoLogo});
  background-size: contain;
  background-repeat: no-repeat;
  width: 100%;
  max-width: 149px;
  flex-shrink: 0;
  margin: 4px 0px 0px 4px;
`;

const VoucherInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  padding: 10px 14px 14px 19px;
`;

const VoucherInfo = styled.div`
  align-self: self-start;
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const VoucherDate = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  align-self: self-end;
  width: 100%;
`;

const DateItemWrapper = styled.div`
  flex-grow: 1;
`;

const DateItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Separation = styled.div`
  position: absolute;
  left: 31.1%;
  top: 54px;
  z-index: 100;
  height: 39%;
  width: 1px;
  border-left: 1px dashed rgb(111, 195, 115);
  transform: scaleY(2);
  transform-origin: center center;
`;

interface Props {
  voucher: Voucher;
  selectedVoucher: Voucher | null;
  handleOnApplyVoucher: (data: any) => void;
  voucherStatus: string | null;
  isDisabled?: boolean | null;
}

const VoucherCard: React.FC<Props> = ({
  voucher,
  handleOnApplyVoucher,
  selectedVoucher,
  voucherStatus,
  isDisabled,
}) => {
  const lang = localStorage.getItem('lang');

  const isVoucherApplying = useMemo(() => {
    return (
      selectedVoucher &&
      selectedVoucher.code === voucher.code &&
      voucherStatus &&
      voucherStatus === VOUCHER_STATUS.APPLIED
    );
  }, [selectedVoucher, voucherStatus, voucher]);

  const renderVoucherStatus = (selectedVoucher: Voucher | null, voucher: Voucher) => {
    if (selectedVoucher && selectedVoucher.code === voucher.code) {
      if (voucherStatus && voucherStatus === VOUCHER_STATUS.APPLIED) {
        return (
          <Typography.Text style={{ fontSize: '14px', fontWeight: 500, color: '#43A047' }}>
            {translations(messages.applied)}
          </Typography.Text>
        );
      }
      if (voucherStatus) {
        return (
          <Flex gap={4} align="center">
            <WarningOutlined style={{ color: '#D97008' }} />
            <Typography.Text style={{ fontSize: '14px', fontWeight: 400, color: '#D97008' }}>
              {translations(messages.invalid)}
            </Typography.Text>
          </Flex>
        );
      }
    }
    if (isDisabled) {
      return (
        <div style={{ flexGrow: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#E2483C', textAlign: 'right' }}>
            {translations(messages.notEligible)}
          </div>
        </div>
      );
    }
    return (
      <Button
        type="bg-primary"
        style={{
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '18px',
          padding: '10px 16px',
          height: 'auto',
          opacity: !!isDisabled ? 0.4 : 1,
        }}
        onClick={(e) => {
          // e.preventDefault();
          handleOnApplyVoucher(voucher);
        }}
        disabled={isDisabled}
      >
        {translations(messages.apply)}
      </Button>
    );
  };
  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isMobile = width < 768;
        return (
          <Wrapper isActive={isVoucherApplying} isDisabled={isDisabled}>
            <VoucherInfoWrapper>
              <KamereoLogoImage />
              <VoucherInfoContent>
                <VoucherInfo>
                  <Typography.Text
                    ellipsis={{ tooltip: voucher[lang === 'vi' ? 'nameVn' : 'nameEn'] }}
                    style={{ maxWidth: 326, fontSize: '16px', fontWeight: 500, color: '#0A0A0B', lineHeight: '26px' }}
                  >
                    {voucher[lang === 'vi' ? 'nameVn' : 'nameEn']}
                  </Typography.Text>
                  <Typography.Text
                    ellipsis={{
                      tooltip: (
                        <FormattedMessage
                          {...messages.voucherDetail}
                          values={{
                            maxDiscount: <Money>{voucher?.discountMaxValue ?? 0}</Money>,
                            minValue: <Money>{voucher?.discountMinPrice ?? 0}</Money>,
                          }}
                        />
                      ),
                    }}
                    style={{ maxWidth: 326, fontSize: '14px', fontWeight: 500, color: '#43A047', lineHeight: '18px' }}
                  >
                    <FormattedMessage
                      {...messages.voucherDetail}
                      values={{
                        maxDiscount: <Money>{voucher?.discountMaxValue ?? 0}</Money>,
                        minValue: <Money>{voucher?.discountMinPrice ?? 0}</Money>,
                      }}
                    />
                  </Typography.Text>
                </VoucherInfo>
                <VoucherDate>
                  <DateItemWrapper>
                    {!!voucher.endDate && (
                      <DateItem>
                        <SVGIcon src={clock} />
                        <Typography.Text
                          ellipsis={{
                            tooltip: `${translations(messages.expiredAt)}: ${handleDateFromGraphQL(voucher.endDate)}`,
                          }}
                          style={{
                            maxWidth: 230,
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#596056',
                            lineHeight: '20px',
                          }}
                        >
                          {`${translations(messages.expiredAt)}: ${handleDateFromGraphQL(voucher.endDate)}`}
                        </Typography.Text>
                      </DateItem>
                    )}
                    <DateItem>
                      <SVGIcon src={gift} />
                      <Typography.Text
                        ellipsis={{ tooltip: `Code: ${voucher.code}` }}
                        style={{
                          maxWidth: 230,
                          fontSize: '14px',
                          fontWeight: 400,
                          color: '#596056',
                          lineHeight: '20px',
                        }}
                      >
                        Code: {voucher.code}
                      </Typography.Text>
                    </DateItem>
                  </DateItemWrapper>
                  {voucherStatus || isDisabled ? (
                    renderVoucherStatus(selectedVoucher, voucher)
                  ) : (
                    <Button
                      type="bg-primary"
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: '18px',
                        padding: '11px 16px',
                        height: 'auto',
                        opacity: !!isDisabled ? 0.4 : 1,
                      }}
                      onClick={(e) => {
                        // e.preventDefault();
                        handleOnApplyVoucher(voucher);
                      }}
                    >
                      {translations(messages.apply)}
                    </Button>
                  )}
                </VoucherDate>
              </VoucherInfoContent>
            </VoucherInfoWrapper>
            <Separation />
          </Wrapper>
        );
      }}
    </Viewport.Consumer>
  );
};

export default VoucherCard;
